import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { } from '@mui/material';
import UpcomingAppointments from '../TopAppointments/index';
import RecentCalls from '../RecentCalls/index';
import RecentNotes from '../RecentNotes/index';
import TimeSheetWrapper from '../../container/Employee/EmployeeHome/EmployeeHomeComponents/Timesheets/TimesheetWrapper';
import SupportCoordinatorManagement from '../../container/Manager/SupportCoordinatorManagement';
import SupportCoordinatorCustomers from '../../container/SupportCoordinator/SupportCoordinatorCustomers';
import EVV_NJ_SUBMISSION_REVIEW from 'src/components/Evv/evv_submission_review';
import EmpLocate from 'src/components/EmpLocate/EmpLocate';
import Documents from 'src/container/Documents/Documents';
import Scheduler from 'src/container/Scheduler/Scheduler';
import FinancialSummary from 'src/components/Billing/FinancialSummary/FinancialSummary';
import Claims from 'src/components/Billing/Claims/Claims';
import Remittances from 'src/components/Billing/Remittances/Remittances';
import BI_Dashboards from 'src/components/Reports_V2/BI_Dashboards';
import Billing from '../Billing/Billing';
import TimeSummaryAlt2 from 'src/container/TimeManagement/TimeSummaryAlt2';
import HomeDashboardKPIs from 'src/components/HomeHealthcare/HomeDashboardKPIs';
import ShiftMap from 'src/components/HomeHealthcare/ShiftMap';
import Alerts from 'src/components/HomeHealthcare/Alerts';
import BillingSummaryChart from 'src/components/HomeHealthcare/BillingSummaryChart';
import CaregiverTurnoverChart from 'src/components/HomeHealthcare/CaregiverTurnoverChart';
import NetPromoterScore from 'src/components/HomeHealthcare/NetPromoterScore';
import axios from 'axios';
import { APIURL } from '../../config';
import RGL, { WidthProvider } from "react-grid-layout";
import UpcomingTasks from 'src/container/Employee/EmployeeHome/EmployeeHomeComponents/UpcomingTasks';
import MonitorDelays from 'src/container/Workflows/DashboardWidgets/MonitorDelays';
import BottleneckTasks from 'src/container/Workflows/DashboardWidgets/BottleneckTasks';
import EmployeeLoad from 'src/container/Workflows/DashboardWidgets/EmployeeLoad';
import ActiveWorkflows from 'src/container/Workflows/ActiveWorkflows';
import AiCompanion from '../AiCompanion/AiCompanion';

const GridLayout = WidthProvider(RGL);

// Passed to GridLayout below and reused in the row height math. 
// RGL's container height is rows * rowHeight + margin.y * (rows - 1) + containerPadding.y * 2.
const GRID_MARGIN = [10, 10];
const GRID_CONTAINER_PADDING = [0, 5];
const MIN_ROW_HEIGHT = 40;


export default function GridLayoutComponent(props) {
    const [editLayout, setEditLayout] = useState(false);
    const [layout, setLayout] = useState([]);
    const [layoutContent, setLayoutContent] = useState({});
    const [rowHeight, setRowHeight] = useState(MIN_ROW_HEIGHT);
    const containerRef = useRef(null);

    const pBusinessType = props.user?.comp_settings.find(setting => setting.feature_value === 'primaryBusinessType') ? props.user.comp_settings.filter((setting) => setting.feature_value === "primaryBusinessType")[0].feature_id : -1;
    const isSubscibedToTimeManagement = props.user.subs.find((setting) => setting.feature_id === 12) ? true : false;

    const rowCount = useMemo(
        () => layout.reduce((max, item) => Math.max(max, item.y + item.h), 0),
        [layout]
    );

    useLayoutEffect(() => {
        if (!containerRef.current || rowCount === 0) {
            return;
        }

        let frame = null;
        const updateRowHeight = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
                // Document-relative, so a recompute while the page happens to be
                // scrolled can't read a smaller offset and inflate the row height.
                const rect = containerRef.current?.getBoundingClientRect();
                const top = rect ? rect.top + window.scrollY : 0;
                const available = window.innerHeight - top
                    - GRID_CONTAINER_PADDING[1] * 2
                    - GRID_MARGIN[1] * (rowCount - 1);
                setRowHeight(Math.max(MIN_ROW_HEIGHT, Math.floor(available / rowCount)));
            });
        };

        updateRowHeight();
        window.addEventListener("resize", updateRowHeight);

        // Catches what resize misses: the sidebar collapsing, widgets above the grid
        // loading in and pushing it down, the tab bar wrapping.
        const observer = new ResizeObserver(updateRowHeight);
        observer.observe(document.body);

        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener("resize", updateRowHeight);
            observer.disconnect();
        };
    }, [rowCount]);

    useEffect(() => {
        const loadGridLayout = async () => {
            try {
                await getGridLayout();
            } catch (error) {
                console.error('Error loading grid layout:', error);
            }
        };
        loadGridLayout();
    }, []);

    const getGridLayout = async () => {
        await axios.get(APIURL + "/gridLayout", {
            params: {
                companyID: props.comp_id,
                companyState: props.user.states[0]?.state_id,
                employeeType: props.user.type,
                LineOfBusiness: pBusinessType
            }
        }).then((response) => {
            const loadedLayout = JSON.parse(response.data[0].layoutConfig);
            setLayout(loadedLayout);
        }).catch((error) => {
            console.error(error);
        });

        await axios.get(APIURL + "/gridLayout/layoutContent", {
            params: {
                companyID: props.comp_id,
                companyState: props.user.states[0]?.state_id,
                employeeType: props.user.type,
                LineOfBusiness: pBusinessType
            }
        }).then((response) => {
            console.log(response.data[0].layoutContent)
            setLayoutContent(JSON.parse(response.data[0].layoutContent));
        }).catch((error) => {
            console.error(error);
        });
    };

    const handleLayoutChange = (newLayout) => {
        setLayout(newLayout);
    };

    const componentMap = {
        "Upcoming Appointments": <UpcomingAppointments user={props.user} />,
        "Upcoming Tasks": <UpcomingTasks user={props.user} />,
        "Recent Notes": <RecentNotes user={props.user} checkTokenExpiry={props.checkTokenExpiry} />,
        "Recent Calls": <RecentCalls user={props.user} />,
        "Timesheets": <TimeSheetWrapper user={props.user} />,
        "EVV Submission Review": <EVV_NJ_SUBMISSION_REVIEW user={props.user} />,
        "Locate": <EmpLocate {...props} comp_id={props.comp_id} checkTokenExpiry={props.checkTokenExpiry} />,
        "Time Management Summary": <TimeSummaryAlt2 {...props} company_id={props.comp_id} checkTokenExpiry={props.checkTokenExpiry} user={props.user} />,
        "Document Center": <Documents {...props} comp_id={props.comp_id} checkTokenExpiry={props.checkTokenExpiry} user={props.user} />,
        "BI & Reporting": <BI_Dashboards {...props} />,
        "Billing": <Billing {...props} checkTokenExpiry={props.checkTokenExpiry} user={props.user} comp_id={props.comp_id} />,
        "Claims": <Claims checkTokenExpiry={props.checkTokenExpiry} user={props.user} comp_id={props.comp_id} />,
        "Remittance": <Remittances checkTokenExpiry={props.checkTokenExpiry} user={props.user} comp_id={props.comp_id} />,
        "Financial Summary": <FinancialSummary checkTokenExpiry={props.checkTokenExpiry} user={props.user} comp_id={props.comp_id} />,
        "Scheduler": <Scheduler
            {...props}
            comp_id={props.comp_id}
            checkTokenExpiry={props.checkTokenExpiry}
            user={props.user}
            selectedView={props.user.type == 'Admin' ? 'resource' : 'employee'}
            headerHeight={240}
        />,
        "SC Customers": <SupportCoordinatorCustomers
            {...props}
            handleShowNote={props.handleShowNote}
            handleShowUpload={props.handleShowUpload}
            handleShowTimesheet={props.handleShowTimesheet}
            data={props.sData ? props.sData : []}
            comp_id={props.comp_id}
            emp_id={props.user.emp_id}
            loading={props.sDataLoading}
            isSubscibedToTimeManagement={isSubscibedToTimeManagement}
            onRegisterFetchRows={props.onRegisterFetchRows}
        />,
        "SC Management": <SupportCoordinatorManagement
            {...props}
            handleShowNote={props.handleShowNote}
            handleShowUpload={props.handleShowUpload}
            data={props.data ? props.data : []}
            comp_id={props.comp_id}
            emp_id={props.user.emp_id}
            getSCH={props.getSCH}
            getEmployees={props.getEmployees}
            updateTaskStatus={props.updateTaskStatus}
            loading={props.dataLoading}
        />,
        "Home Dashboard KPIs": <HomeDashboardKPIs user={props.user} comp_id={props.comp_id} />,
        "Shift Map": <ShiftMap user={props.user} comp_id={props.comp_id} />,
        "Alerts": <Alerts user={props.user} comp_id={props.comp_id} />,
        "Billing Summary Chart": <BillingSummaryChart user={props.user} comp_id={props.comp_id} />,
        "Caregiver Turnover Chart": <CaregiverTurnoverChart user={props.user} comp_id={props.comp_id} />,
        "Net Promoter Score": <NetPromoterScore user={props.user} comp_id={props.comp_id} />,
        "Monitor Delays": <MonitorDelays user={props.user} comp_id={props.comp_id} />,
        "Bottleneck Tasks": <BottleneckTasks user={props.user} comp_id={props.comp_id} />,
        "Employee Load": <EmployeeLoad user={props.user} comp_id={props.comp_id} />,
        "Workflow Instances": <ActiveWorkflows {...props} user={props.user} comp_id={props.comp_id} />,
        "AI Companion": <AiCompanion {...props} checkTokenExpiry={props.checkTokenExpiry} user={props.user} comp_id={props.comp_id} />,
    };

    return (
        <div ref={containerRef} style={editLayout ? { marginBottom: '100px' } : {}}>
            <GridLayout
                className="layout"
                layout={layout}
                cols={12}
                rowHeight={rowHeight}
                margin={GRID_MARGIN}
                containerPadding={GRID_CONTAINER_PADDING}
                isDraggable={editLayout}
                isResizable={editLayout}
                resizeHandles={['s', 'w', 'e', 'n', 'sw', 'nw', 'se', 'ne']}
                onLayoutChange={handleLayoutChange}
            >
                {layout.map((item) => {
                    const Component = componentMap[layoutContent[item.i]];

                    return (
                        <div
                            key={item.i}
                            style={{ border: '1px solid lightgray', borderRadius: '5px', boxShadow: '0 0 5px lightgray', display: layoutContent[item.i] === 'Scheduler' ? 'flex' : '' }}
                        >
                            <div style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
                                {Component ? Component : null}
                            </div>
                            {layoutContent[item.i] === 'Scheduler' &&
                                <div style={{ width: '15%', borderLeft: '1px solid #a3a3a3', paddingLeft: '10px' }}>
                                    <p style={{ fontWeight: '600' }}>Customer Present</p>
                                    {
                                        props.presentCustomers?.length > 0 ?
                                            <ul style={{ paddingLeft: '20px' }}>
                                                {props.presentCustomers.map((customer, index) => (
                                                    <li key={index}>{customer.cus_name}</li>
                                                ))}
                                            </ul> :
                                            <p>No customers in any session yet</p>
                                    }
                                </div>
                            }
                        </div>
                    );
                })}
            </GridLayout>
        </div>
    );
};